import { FC, ReactElement, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { AlertTriangle, Loader2 } from "lucide-react";
import { CopropertyDto, CopropertyQuestionnaireResponse } from "api/coproperty";
import { QuestionAnswerDto } from "api/risk";
import { copropertiesApi } from "services/swagger-api";
import { useAppDispatch, useAppSelector } from "app/hooks";
import RadioButtonChoice, { Sizes } from "components/domain/ProfilingForm/RadioButtonChoice";
import { Button } from "components/ui/Button";
import { ContextCode, SurveyName, useGenericSurvey } from "../profiling-process/hooks/useGenericSurvey";
import { useCBNOProfiling } from "../profiling-process/hooks/cbno/useCBNOProfiling";
import { setExternalAnswer } from "../profiling-process/slices/genericProfilingSlice";
import { selectEligibilityRules, selectProfilingConfig } from "../profiling-process/slices/genericSelectors";
import { getQuestionEligibilityMessage } from "../profiling-process/slices/utils/profilingUtils";
import { ProfilingMode } from "../profiling-process/slices/types/enums";
import { PossibleAnswer, Question } from "../profiling-process/types/interfaces";
import { isLabelAnswer } from "../profiling-process/types/possibleAnswer";
import { CopropertySection } from "./CopropertyStepLayout";
import { CopropertySurveyBlocksRail } from "./CopropertySurveyBlocksRail";

/**
 * Clé de question injectée comme réponse externe depuis l'entité copropriété : elle
 * pilote la visibilité du bloc travaux (construite depuis plus de 10 ans) et doit être
 * utilisée telle quelle dans le seed du questionnaire COPRO_MRI. L'ancienne clé
 * OCCUPATION_MOINS_50 (question mur) a disparu avec la spec du 2026-08-31 : la question
 * des accès murés vit sur les bâtiments.
 */
export const COPRO_EXTERNAL_QUESTION_KEYS = {
  constructionPlusDe10Ans: "CONSTRUCTION_PLUS_10_ANS",
} as const;

/**
 * Ligne de question du bloc : libellé à gauche, réponses en pills à droite. Le
 * questionnaire COPRO_MRI ne contient que des booléens et des choix uniques à libellé.
 */
const SurveyQuestionRow: FC<{
  question: Question;
  currentAnswers: PossibleAnswer[];
  isLast: boolean;
  /** Message de la règle de refus déclenchée par la réponse courante, s'il y en a une. */
  warning?: string;
  onAnswer: (key: string, answer: PossibleAnswer[]) => void;
}> = ({ question, currentAnswers, isLast, warning, onAnswer }) => {
  const renderAnswers = () => {
    if (question.type === "boolean") {
      return [true, false].map((choice) => (
        <RadioButtonChoice
          key={`${question.key}-${choice}`}
          size={Sizes.md}
          radio
          text={choice ? "Oui" : "Non"}
          selected={currentAnswers.includes(choice)}
          handleOnClick={() => onAnswer(question.key, [choice])}
        />
      ));
    }

    return question.possibleAnswers.filter(isLabelAnswer).map((answer) => (
      <RadioButtonChoice
        key={`${question.key}-${answer.key}`}
        size={Sizes.md}
        radio
        text={answer.label}
        selected={currentAnswers.some((current) => isLabelAnswer(current) && current.key === answer.key)}
        handleOnClick={() => onAnswer(question.key, [answer])}
      />
    ));
  };

  return (
    <div className={`flex flex-col gap-2 py-3.5 ${isLast ? "" : "border-b border-gray-100"}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
        <p className="flex-1 text-[15px] text-zinc-800">{question.label}</p>
        <div className="flex flex-none flex-wrap gap-2">{renderAnswers()}</div>
      </div>
      {warning && (
        <p className="flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-[13px] text-red-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
          <span>{warning}</span>
        </p>
      )}
    </div>
  );
};

/** Navigation du questionnaire, portée par les boutons de la page ou du panneau. */
export interface SurveyStepControls {
  prevLabel: string;
  onPrevious: () => void;
  /** Premier bloc sans sortie arrière (panneau) : le bouton « Précédent » n'a rien à faire. */
  prevHidden?: boolean;
  nextLabel: string;
  nextDisabled: boolean;
  onNext: () => void;
}

/** Morceaux du questionnaire, à disposer par la page ou le panneau qui l'accueille. */
export interface CopropertySurveyRenderParts {
  /** Rail d'avancement des blocs (colonne de gauche de la page, en-tête du panneau). */
  rail: ReactNode;
  /** Message d'aide du bloc courant (facultatif ou éliminatoire). */
  message: string;
  /** Bloc courant : titre, questions, alerte de refus. */
  content: ReactNode;
}

interface CopropertySurveyEngineProps {
  coproperty: CopropertyDto;
  /** Réponses déjà enregistrées, rechargées dans le store à l'ouverture. */
  savedAnswers?: QuestionAnswerDto[];
  /** Questionnaire enregistré et copropriété éligible. */
  onCompleted: (result: CopropertyQuestionnaireResponse) => void;
  /** Verdict de refus du serveur : motifs à afficher (sortie de parcours, toast…). */
  onRefused: (reasons: string[]) => void;
  /** Sortie arrière depuis le premier bloc ; absente, le bouton « Précédent » est masqué. */
  onExitBack?: () => void;
  /** Remonte les libellés et actions des boutons de navigation pour le bloc courant. */
  onControlsChange: (controls: SurveyStepControls) => void;
  children: (parts: CopropertySurveyRenderParts) => ReactElement;
}

/**
 * Moteur du questionnaire COPRO_MRI : chargement des blocs, réponses externes, navigation
 * bloc par bloc, enregistrement et verdict — sans cadre ni navigation de route, pour tourner
 * aussi bien dans la sous-étape du parcours que dans un panneau de la contractualisation.
 * Les kill questions sont évaluées deux fois : côté front dès la réponse, avec les règles du
 * questionnaire exposées par le back (message sous la question, bloc en refus dans le rail,
 * sortie anticipée possible), et côté serveur à la validation — seul ce dernier verdict fait
 * foi : non éligible → `onRefused`.
 */
export const CopropertySurveyEngine: FC<CopropertySurveyEngineProps> = ({
  coproperty,
  savedAnswers,
  onCompleted,
  onRefused,
  onExitBack,
  onControlsChange,
  children,
}) => {
  const dispatch = useAppDispatch();
  const { initSurvey, mapFromSurveyFormValue, mapToSurveyFormValue } = useGenericSurvey();
  const eligibilityRules = useAppSelector(selectEligibilityRules);
  const questionsGroups = useAppSelector(selectProfilingConfig).questionsGroups;
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Mode Group : toutes les questions du bloc sur un écran, pas d'auto-advance.
  useEffect(() => {
    initSurvey(ContextCode.Coproperty, SurveyName.Coproperty, ProfilingMode.Group);
  }, [initSurvey]);

  // Réponses déjà enregistrées (session COPRO_MRI renvoyée par le process du risque) : on les
  // recharge dans le store à chaque chargement du questionnaire, car ce chargement remet les
  // réponses à zéro (retour par le fil d'Ariane avec un store encore garni, par exemple).
  const hydratedForGroups = useRef<typeof questionsGroups | null>(null);
  useEffect(() => {
    if (!savedAnswers?.length || questionsGroups.length === 0) return;
    if (hydratedForGroups.current === questionsGroups) return;
    hydratedForGroups.current = questionsGroups;
    mapToSurveyFormValue(savedAnswers);
  }, [savedAnswers, questionsGroups, mapToSurveyFormValue]);

  // Réponse externe injectée depuis l'entité : condition de visibilité du bloc travaux.
  useEffect(() => {
    if (coproperty.builtMoreThan10YearsAgo != null) {
      dispatch(
        setExternalAnswer({
          questionKey: COPRO_EXTERNAL_QUESTION_KEYS.constructionPlusDe10Ans,
          value: [coproperty.builtMoreThan10YearsAgo],
        })
      );
    }
  }, [dispatch, coproperty.builtMoreThan10YearsAgo]);

  const handleSubmit = useCallback(
    async (formattedAnswers: Record<string, unknown>) => {
      if (!coproperty.id) return;

      setIsSubmitting(true);
      try {
        // Le hook fournit une valeur par clé ; le mapper attend le format brut (tableaux) et,
        // pour les questions à choix, la clé de la réponse plutôt que l'objet réponse stocké
        // par les lignes du questionnaire (sinon la réponse est ignorée à l'enregistrement).
        const rawAnswers: Record<string, PossibleAnswer[]> = Object.fromEntries(
          Object.entries(formattedAnswers).map(([key, value]) => [
            key,
            value == null ? [] : [isLabelAnswer(value as PossibleAnswer) ? (value as { key: string }).key : (value as PossibleAnswer)],
          ])
        );
        const answers = mapFromSurveyFormValue(rawAnswers).map((answer) => ({
          questionId: answer.questionId,
          possibleAnswerId: answer.possibleAnswerId ?? undefined,
          answerText: answer.answerText ?? undefined,
        }));

        const result = await copropertiesApi.saveCopropertyQuestionnaire({
          copropertyId: coproperty.id,
          saveCopropertyQuestionnaireRequest: { answers },
        });

        if (!result.isSuccess) {
          toast.error(result.errorMessage || "L'enregistrement du questionnaire a échoué.");
          return;
        }

        if (!result.isEligible) {
          onRefused(result.reasons ?? []);
          return;
        }

        toast.success("Questionnaire enregistré — la copropriété est éligible.");
        onCompleted(result);
      } catch (error) {
        console.error(error);
        toast.error("L'enregistrement du questionnaire a échoué.");
      } finally {
        setIsSubmitting(false);
      }
    },
    [coproperty.id, mapFromSurveyFormValue, onCompleted, onRefused]
  );

  const {
    currentStep,
    visibleSteps,
    currentQuestionGroup,
    questionsToShow,
    answers,
    isFirstStep,
    isLastStep,
    isCurrentQuestionAnswered,
    isCurrentStepSkippable,
    eligibilityResult,
    handleQuestionAnswer,
    handleNext,
    handlePrevious,
    handleSkip,
  } = useCBNOProfiling(handleSubmit);

  /** Règle de refus déclenchée par les réponses données jusqu'ici (alerte front). */
  const refusingRule = eligibilityResult && !eligibilityResult.isEligible ? eligibilityResult.rule : undefined;
  const refusedQuestionKeys = useMemo(
    () => refusingRule?.constraints.map((constraint) => constraint.questionKey) ?? [],
    [refusingRule]
  );

  /** Message de refus sous la question dont la réponse courante déclenche une règle. */
  const warningFor = useCallback(
    (question: Question): string | undefined => {
      const answer = answers[question.key]?.[0];
      if (answer === undefined) return undefined;
      return getQuestionEligibilityMessage(question.key, answer, eligibilityRules);
    },
    [answers, eligibilityRules]
  );

  /**
   * Sortie anticipée : enregistre les réponses données jusqu'ici, le serveur prononce le
   * refus et le motif s'affiche — inutile de finir les blocs.
   */
  const finishNow = useCallback(() => {
    const formattedAnswers: Record<string, unknown> = {};
    Object.entries(answers).forEach(([key, values]) => {
      formattedAnswers[key] = values && values.length > 0 ? values[0] : null;
    });
    void handleSubmit(formattedAnswers);
  }, [answers, handleSubmit]);

  // Dans un bloc facultatif, seules les questions obligatoires conditionnent l'avance :
  // le sélecteur générique (toutes les questions répondues) est trop strict ici.
  const requiredAnswered = useMemo(
    () =>
      questionsToShow
        .filter((question) => question.isRequired !== false)
        .every((question) => (answers[question.key] ?? []).length > 0),
    [questionsToShow, answers]
  );
  const nextDisabled = isSubmitting || !requiredAnswered;

  // Bloc entièrement répondu : chemin nominal. Bloc facultatif partiellement rempli :
  // on avance via le skip (les réponses déjà données restent enregistrées).
  const handleNextBlock = useCallback(() => {
    if (isCurrentQuestionAnswered) {
      handleNext();
    } else {
      handleSkip();
    }
  }, [isCurrentQuestionAnswered, handleNext, handleSkip]);

  // Les boutons de la page (ou du panneau) pilotent les blocs : libellés et actions suivent le bloc courant.
  useEffect(() => {
    onControlsChange({
      prevLabel: isFirstStep ? "Retour à la copropriété" : "Bloc précédent",
      onPrevious: isFirstStep ? onExitBack ?? (() => undefined) : handlePrevious,
      prevHidden: isFirstStep && !onExitBack,
      nextLabel: isLastStep ? "Valider le questionnaire" : "Bloc suivant",
      nextDisabled: nextDisabled || !currentQuestionGroup,
      onNext: handleNextBlock,
    });
  }, [onControlsChange, isFirstStep, isLastStep, nextDisabled, currentQuestionGroup, onExitBack, handlePrevious, handleNextBlock]);

  if (!currentQuestionGroup) {
    return (
      <div className="flex items-center justify-center gap-2 p-8 text-gray-500">
        <Loader2 className="h-5 w-5 animate-spin" />
        Chargement du questionnaire…
      </div>
    );
  }

  return children({
    rail: (
      <CopropertySurveyBlocksRail
        visibleSteps={visibleSteps}
        currentStep={currentStep}
        answers={answers}
        refusedQuestionKeys={refusedQuestionKeys}
      />
    ),
    message: isCurrentStepSkippable
      ? "Ce bloc est facultatif — répondez à ce que vous savez, le reste peut attendre."
      : "Répondez pour l'ensemble de la copropriété — ces réponses déterminent son éligibilité.",
    content: (
      <CopropertySection
        title={currentQuestionGroup.title || "Questions"}
        titleAside={
          isCurrentStepSkippable ? (
            <Button outline color="primary" disabled={isSubmitting} onClick={handleSkip}>
              Passer ce bloc
            </Button>
          ) : undefined
        }
      >
        <div className="flex flex-col">
          {questionsToShow.map((question, index) => (
            <SurveyQuestionRow
              key={question.key}
              question={question}
              currentAnswers={answers[question.key] ?? []}
              isLast={index === questionsToShow.length - 1}
              warning={warningFor(question)}
              onAnswer={handleQuestionAnswer}
            />
          ))}
        </div>

        {refusingRule && (
          <div className="flex flex-col gap-3 rounded-xl border border-red-200 bg-red-50 p-4 sm:flex-row sm:items-center">
            <div className="flex flex-1 gap-2.5">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-none text-red-700" />
              <div className="text-[13px] text-red-700">
                <p className="font-bold">Avec ces réponses, la copropriété ne sera pas éligible</p>
                <p>
                  {refusingRule.message || refusingRule.reason || "Une réponse déclenche une exclusion du produit."}{" "}
                  Corrigez la réponse si elle est erronée, ou terminez maintenant pour voir le motif.
                </p>
              </div>
            </div>
            <Button outline color="primary" disabled={isSubmitting} onClick={finishNow}>
              Terminer maintenant
            </Button>
          </div>
        )}
      </CopropertySection>
    ),
  });
};
